<script setup lang="ts">
import { FairIconButton } from '@/ui/primitives/FairIconButton'

/**
 * RemovableChip — DS "Controls" › Chip › RemovableChip (399:23104).
 * fill/chip pill, body/ui secondary label, × icon button (32 / Mobile 44).
 * With logo: 24px (Mobile 36px) media on white; a colour dot works the same way.
 * `openable` makes the label a button (e.g. reopen the filter the chip came from).
 */
interface FairRemovableChipProps {
  label: string
  size?: 'default' | 'mobile'
  imageSrc?: string | null
  color?: string | null
  openable?: boolean
  removeLabel?: string
  disabled?: boolean
  /** `false` — the logo chip without the remove button (Measurements › More measurements). */
  removable?: boolean
}

withDefaults(defineProps<FairRemovableChipProps>(), {
  size: 'default',
  imageSrc: null,
  color: null,
  openable: false,
  removeLabel: '',
  disabled: false,
  removable: true,
})

const emit = defineEmits<{
  remove: []
  open: []
}>()
</script>

<template>
  <span
    class="fair-removable-chip"
    :class="{
      'fair-removable-chip--mobile': size === 'mobile',
      'fair-removable-chip--with-media': imageSrc || color,
      'fair-removable-chip--static': !removable,
    }"
  >
    <img v-if="imageSrc" class="fair-removable-chip__media fair-removable-chip__media--logo" :src="imageSrc" :alt="label">
    <span
      v-else-if="color"
      class="fair-removable-chip__media"
      :style="{ background: color }"
      aria-hidden="true"
    />

    <button
      v-if="openable"
      type="button"
      class="fair-removable-chip__label fair-removable-chip__label--button"
      :disabled="disabled"
      @click="emit('open')"
    >
      {{ label }}
    </button>
    <span v-else class="fair-removable-chip__label">{{ label }}</span>

    <FairIconButton
      v-if="removable"
      icon="close"
      :size="size === 'mobile' ? 'mobile' : 'small'"
      :aria-label="removeLabel || label"
      :disabled="disabled"
      @click="emit('remove')"
    />
  </span>
</template>
